// SessionSummary.tsx
//
// Post-cook summary screen. Reached at /cook/:sessionId/summary after session
// completion. Fullscreen — NOT wrapped in AppShell.
//
// Data sourcing:
//   - cookRating / dishRating / cookNote come from router state (passed by
//     CookingMode when it navigates here after completion). On a hard refresh
//     the state is lost; we default gracefully ("Not rated").
//   - The session itself (recipeId, startedAt, completedAt) is fetched via
//     getCookSession(sessionId). Completed sessions are NOT in the Dexie open
//     set (completeCookSessionOptimistic deletes them), but the API returns
//     them fine.
//   - The recipe (name/cover) is fetched via useRecipe(recipeId) (Dexie-cached).
//   - Cook logs for the recipe are fetched via getCookLogsByRecipe to drive
//     the "What we noticed" insights section and interactive dish rating patch.
//
// NOTE: Total time shown here is elapsed (completedAt - startedAt). The
// active-vs-elapsed breakdown is refined in Plan 07; for now elapsed = total.

import type { JSX } from "react";
import { useEffect, useRef, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router";
import { toast } from "sonner";
import {
  localDateInZone,
  type CookSessionApiItem,
  type CookLogApiItem,
} from "@recipevault/shared";
import { getCookSession } from "./api.js";
import { getCookLogsByRecipe, patchCookLog } from "./cook-log-api.js";
import { deriveCookInsights } from "./cookInsights.js";
import { LeftoverCapture } from "./LeftoverCapture.js";
import { friendlyError } from "../../shared/http.js";
import { cn } from "../../shared/utils.js";
import { sync } from "../../sync/SyncService.js";
import { useHouseholdTimezone } from "../auth/useHouseholdTimezone.js";
import { useRecipe } from "../recipes/useRecipe.js";
import Rating from "../recipes/Rating.js";
import type { RatingValue } from "../recipes/Rating.js";

/** Recipe notes hard cap — mirrors NotesSchema in packages/shared/src/schemas/recipe.ts. */
const NOTES_MAX_LENGTH = 10240;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Format elapsed minutes as "Hh Mm" or "Nm" */
function formatElapsed(minutes: number): string {
  const rounded = Math.round(minutes);
  if (rounded < 60) return `${rounded}m`;
  const h = Math.floor(rounded / 60);
  const m = rounded % 60;
  return m === 0 ? `${h}h` : `${h}h ${m}m`;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

interface SummaryRouterState {
  cookRating?: RatingValue;
  dishRating?: RatingValue;
  cookNote?: string | null;
}

export function SessionSummary(): JSX.Element {
  const { sessionId } = useParams<{ sessionId: string }>();
  const location = useLocation();
  const navigate = useNavigate();

  const state = (location.state ?? {}) as SummaryRouterState;
  const cookRating = state.cookRating ?? null;
  const dishRating = state.dishRating ?? null;

  const tz = useHouseholdTimezone();
  const today = localDateInZone(tz, new Date());

  // Deterministic log ID: mirrors backend's logIdForSession
  const logId = sessionId ? `log_${sessionId.slice("sess_".length)}` : null;

  // Fetch the completed session for recipeId + timing
  const [session, setSession] = useState<CookSessionApiItem | null>(null);

  useEffect(() => {
    if (!sessionId) return;
    getCookSession(sessionId)
      .then(setSession)
      .catch(() => {
        // Session fetch failed — UI degrades gracefully (title/time absent)
      });
  }, [sessionId]);

  // Dexie-cached recipe (live query)
  const { recipe } = useRecipe(session?.recipeId);

  // Cook log list — drives insights + interactive dish rating patch
  const [cookLogs, setCookLogs] = useState<CookLogApiItem[] | null>(null);
  const [logsError, setLogsError] = useState(false);

  useEffect(() => {
    if (!session?.recipeId) return;
    getCookLogsByRecipe(session.recipeId)
      .then((res) => { setCookLogs(res.items); })
      .catch(() => { setLogsError(true); });
  }, [session?.recipeId]);

  // thisLog: the just-finished cook's persisted log row
  const thisLog: CookLogApiItem | null = cookLogs?.find((l) => l.id === logId) ?? null;

  // Derive insight lines (empty when logs not yet loaded or no prior cooks)
  const insights: string[] = cookLogs
    ? deriveCookInsights(cookLogs, logId ?? "", recipe?.steps ?? [])
    : [];

  // Interactive dish rating — optimistic local state; PATCHes on change.
  // Seeded from router state initially, then reconciled to the persisted log
  // value once the cook-log list loads (survives a refresh that loses router
  // state). userEditedRef prevents that reconcile from clobbering an in-progress
  // edit the user made before the list resolved.
  const [dishRatingValue, setDishRatingValue] = useState<RatingValue>(dishRating);
  const [patchError, setPatchError] = useState<string | null>(null);
  const userEditedRef = useRef(false);

  // thisLog identity changes when the cook-log list resolves; dishRating
  // (router state) is stable for a given navigation.
  useEffect(() => {
    if (thisLog && !userEditedRef.current) {
      setDishRatingValue(thisLog.dishRating ?? dishRating);
    }
  }, [thisLog, dishRating]);

  function handleDishRatingChange(next: RatingValue): void {
    userEditedRef.current = true;
    setDishRatingValue(next);
    if (!logId || !thisLog) return; // log not yet loaded — update UI only
    const prev = dishRatingValue;
    setPatchError(null);
    void patchCookLog(logId, { completedAt: thisLog.completedAt, dishRating: next }).catch(
      (err: unknown) => {
        setDishRatingValue(prev); // revert on failure
        setPatchError(friendlyError(err));
      },
    );
  }

  // "Save to notes" — one-tap append of a cook-insight line onto the recipe's
  // notes, dated with the household-tz "today". Tracked per-insight (keyed by
  // the insight's own text, matching the list's `key={line}`) so only the
  // tapped line's button flips to "Saved ✓"; a VERSION_CONFLICT (or any other
  // patch failure) leaves the button as-is — patchRecipeOptimistic already
  // refetches canonical state + toasts the user on conflict (SyncService.ts),
  // so we don't retry or duplicate that toast here.
  const [savedInsights, setSavedInsights] = useState<Set<string>>(new Set());

  function handleSaveInsight(insight: string): void {
    if (!recipe) return;
    const stamp = `[cooked ${today}] ${insight}`;
    const newNotes = recipe.notes ? `${recipe.notes}\n\n${stamp}` : stamp;
    if (newNotes.length > NOTES_MAX_LENGTH) {
      toast.error("Notes are full");
      return;
    }
    void sync
      .patchRecipeOptimistic(recipe.id, { version: recipe.version, notes: newNotes })
      .then(() => {
        setSavedInsights((prev) => new Set(prev).add(insight));
      })
      .catch(() => {
        // VERSION_CONFLICT / network error: SyncService already refetched the
        // canonical row (recipe.version self-updates via the live query) and
        // toasted the user. Leave this button as "Save to notes" — no retry.
      });
  }

  // Compute elapsed time
  let elapsedLabel: string | null = null;
  if (session?.startedAt && session?.completedAt) {
    const elapsedMs =
      new Date(session.completedAt).getTime() -
      new Date(session.startedAt).getTime();
    // NOTE: elapsed = total for now; Plan 07 splits active vs elapsed.
    elapsedLabel = formatElapsed(elapsedMs / 60_000);
  }

  return (
    <main className="min-h-screen flex flex-col items-center justify-start bg-zinc-950 text-zinc-100 px-4 py-12 gap-8">
      {/* Header */}
      <header className="w-full max-w-md text-center">
        <h1 className="text-2xl font-bold tracking-tight">
          {recipe ? recipe.title : session ? " " : "Loading…"}
        </h1>
        <p className="mt-1 text-zinc-400 text-sm">Session complete</p>
      </header>

      {/* Cook rating (read-only) */}
      <section className="w-full max-w-md flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
          Cook rating
        </h2>
        {cookRating !== null ? (
          <Rating value={cookRating} readOnly />
        ) : (
          <p className="text-zinc-400 text-sm">Not rated</p>
        )}
      </section>

      {/* Dish rating (interactive — PATCHes cook log) */}
      <section className="w-full max-w-md flex flex-col gap-2">
        <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
          Dish rating
        </h2>
        {thisLog ? (
          <Rating value={dishRatingValue} onChange={handleDishRatingChange} />
        ) : (
          <Rating value={dishRatingValue} readOnly />
        )}
        {patchError !== null && (
          <p className="text-red-400 text-xs">{patchError}</p>
        )}
      </section>

      {/* Total time */}
      {elapsedLabel !== null && (
        <section className="w-full max-w-md flex flex-col gap-2">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            Total time
          </h2>
          <p className="text-zinc-100 text-lg font-semibold">{elapsedLabel}</p>
        </section>
      )}

      {/* Leftovers? — portion capture + one-tap plan-tomorrow follow-up.
          Only mountable once both the deterministic log ID AND its
          persisted row are available (thisLog carries completedAt, the SK
          prefix patchCookLog needs, and the seed value for the chips). */}
      {logId !== null && thisLog !== null && session !== null && (
        <LeftoverCapture
          logId={logId}
          completedAt={thisLog.completedAt}
          session={session}
          recipeTitle={recipe?.title ?? ""}
          leftoverPortions={thisLog.leftoverPortions ?? null}
        />
      )}

      {/* "What we noticed" learning loop */}
      {logsError ? (
        <p className="w-full max-w-md text-zinc-500 text-sm">
          Couldn't load cook stats.
        </p>
      ) : insights.length > 0 ? (
        <section className="w-full max-w-md flex flex-col gap-3">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500">
            What we noticed
          </h2>
          <ul className="flex flex-col gap-1.5">
            {insights.map((line) => {
              const saved = savedInsights.has(line);
              return (
                <li
                  key={line}
                  className="flex items-center justify-between gap-3 text-zinc-300 text-sm"
                >
                  <span>{line}</span>
                  <button
                    type="button"
                    disabled={saved}
                    onClick={() => { handleSaveInsight(line); }}
                    className={cn(
                      "shrink-0 min-h-12 rounded-lg border px-3 text-xs font-medium transition-colors disabled:cursor-default",
                      saved
                        ? "border-emerald-800 bg-emerald-900/20 text-emerald-400"
                        : "border-zinc-700 text-zinc-300 hover:border-zinc-600 hover:bg-zinc-900",
                    )}
                  >
                    {saved ? "Saved ✓" : "Save to notes"}
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      ) : null}

      {/* Done */}
      <div className="w-full max-w-md pt-4">
        <button
          type="button"
          className="w-full h-12 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-semibold transition-colors"
          onClick={() => { void navigate("/"); }}
        >
          Done
        </button>
      </div>
    </main>
  );
}
